import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProducts, 
  selectProductsState, 
  resetProductsState,
  addToCart,
  fetchCartItems,
  checkLoginStatus,
  selectUserInfo,
  selectIsAdminLoggedIn,
  selectIsUserLoggedIn,
  selectUser
} from '../redux/WithcookieSlice';
import { formatPrice, formatKRW } from '../utils/format';
import { PriceDisplay, DiscountRatePriceDisplay } from './common/PriceDisplay';
import SafeImage from './common/SafeImage';
import { useTranslation } from '../redux/TranslationHelper';

const ProductListComponent = ({ componentProps }) => {
  // 다국어 지원 훅 사용
  const { t } = useTranslation();
  
  const { 
    order = "", 
    lg = 12, 
    className = "padding-tb-40",
    itemsPerRow = 4, // 한 줄에 표시할 아이템 수 (기본값 4)
    categoryId = '',  // 카테고리 ID 추가
    excludeIndexes = [], // 제외할 인덱스 배열
    showStock = false, // 재고 표시 여부 옵션 추가
    include_product_ids = [], // 포함하고 싶은 상품 ID 목록
    exclude_product_ids = [], // 제외하고 싶은 상품 ID 목록
    include_category_ids = [], // 포함하고 싶은 카테고리 ID 목록
    exclude_category_ids = [], // 제외하고 싶은 카테고리 ID 목록
    itemsPerPage = 20 // 한 페이지당 보여줄 아이템 수 (기본값 20)
  } = componentProps || {};
  
  const dispatch = useDispatch();
  const { products, loading, error } = useSelector(selectProductsState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  
  const [isGridView, setIsGridView] = useState(false);
  const [xlValue, setXlValue] = useState(getXlValue(itemsPerRow)); // 초기값을 함수로 설정
  
  // 페이징 관련 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // 디버깅용 카운터
  const [renderCount, setRenderCount] = useState(0);
  
  // 초기 로드 여부 추적
  const initialLoadCompletedRef = useRef(false);
  // 이전 필터 옵션을 저장하기 위한 ref
  const prevFiltersRef = useRef(null);
  
  // 필터 옵션을 useMemo로 메모이제이션
  const filterOptions = useMemo(() => {
    return {
      categoryId,
      include_product_ids,
      exclude_product_ids,
      include_category_ids,
      exclude_category_ids,
      currentPage,
      itemsPerPage
    };
  }, [categoryId, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, currentPage, itemsPerPage]);

  // 컴포넌트 CSS 스타일
  const productImageStyle = {
    imgContainer: {
      width: '100%',
      position: 'relative',
      paddingTop: '100%', // 1:1 비율 유지
      overflow: 'hidden',
      backgroundColor: '#f8f8f8',
      borderRadius: '0', // 둥근 모서리 제거
    },
    imgWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    imageLink: {
      width: "100%",
      height: "100%",
      display: "block",
      position: "relative",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      position: "absolute",
      top: 0,
      left: 0,
      transition: "opacity 0.3s ease",
    },
    hoverImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      position: "absolute",
      top: 0,
      left: 0,
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    productAction: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      background: 'rgba(167, 73, 255, 0.9)',
      padding: '10px',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'all 0.3s ease',
      zIndex: 9
    },
    actionButton: {
      background: 'transparent',
      border: 'none',
      color: 'white',
      fontSize: '16px',
      padding: '8px 12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }
  };

  // 커스텀 CSS 클래스
  const customCSS = `
    .product-hover-area:hover .product-action {
      opacity: 1;
      transform: translateY(0);
    }
    .product-action button:hover {
      transform: scale(1.1);
    }
    .gi-pro-title {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      min-height: 2.7em;
      line-height: 1.5;
      margin-bottom: 8px;
    }
    .gi-pro-title a {
      display: block;
      word-break: break-word;
      overflow-wrap: break-word;
      color: inherit;
    }
    .gi-pro-pagination ul.pagination li {
      margin: 0 5px;
    }
    .gi-pro-pagination ul.pagination li a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 4px;
      background-color: #f5f5f5;
      color: #333;
      text-decoration: none;
      transition: all 0.3s ease;
    }
    .gi-pro-pagination ul.pagination li.active a {
      background-color: #6c4bd1;
      color: white;
    }
    .gi-pro-pagination ul.pagination li a:hover {
      background-color: #e0e0e0;
    }
    .gi-pro-pagination ul.pagination li.active a:hover {
      background-color: #5a3ec0;
    }
    .gi-pro-pagination ul.pagination li.disabled a {
      opacity: 0.5;
      pointer-events: none;
    }
  `;

  const handleAddToCart = async (product) => {
    try {
      // 로그인 체크 - Redux 상태만 사용
      if (!isUserLoggedIn) {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
        return;
      }
      
      
      // 장바구니에 상품 추가
      await dispatch(addToCart({ productId: product.id, count: 1 })).unwrap();
      await dispatch(fetchCartItems());
      alert(t('상품') + ` "${product.title}"` + t('이(가) 장바구니에 추가되었습니다.'));
    } catch (error) {
      console.error(t('장바구니 추가 중 오류:'), error);
      
      // 오류가 인증 관련 문제인 경우
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
      } else {
        alert(t('장바구니 추가에 실패했습니다. 다시 시도해주세요.'));
      }
    }
  };

  // 이미지 호버 이벤트 핸들러
  const handleImageHover = (e, isHover) => {
    // 이미지 요소가 존재하는 경우에만 호버 효과 적용
    const mainImage = e.currentTarget.querySelector('.main-image');
    const hoverImage = e.currentTarget.querySelector('.hover-image');
    
    // 이미지가 존재하는 경우에만 호버 효과 적용
    if (mainImage && hoverImage) {
      mainImage.style.opacity = isHover ? '0' : '1';
      hoverImage.style.opacity = isHover ? '1' : '0';
    }
  };
  
  // 페이지 변경 함수
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) {
      return;
    }
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // 페이지 상단으로 스크롤
  };
  
  // 필터 옵션 객체 생성
  const getFilterOptions = () => {
    const options = {
      page: currentPage,
      per_page: itemsPerPage
    };
    
    // 모든 값을 숫자 배열로 변환하는 함수
    const toIntArray = (value) => {
      // 값이 없으면 빈 배열 반환
      if (!value) return [];
      
      // 이미 배열인 경우
      if (Array.isArray(value)) {
        // 배열의 각 요소를 정수로 변환
        return value
          .map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id))
          .filter(id => !isNaN(id));
      }
      
      // 문자열이나 단일 값인 경우 정수로 변환하여 배열에 넣음
      const parsed = typeof value === 'string' ? parseInt(value, 10) : Number(value);
      return !isNaN(parsed) ? [parsed] : [];
    };

    
    // ID 관련 필드는 항상 정수 배열로 변환
    const includeProductIds = toIntArray(include_product_ids);
    const excludeProductIds = toIntArray(exclude_product_ids);
    const includeCategoryIds = toIntArray(include_category_ids);
    const excludeCategoryIds = toIntArray(exclude_category_ids);
    
    // 배열이 비어있지 않은 경우에만 옵션에 추가
    if (includeProductIds.length > 0) {
      options.include_product_ids = includeProductIds;
      
    }
    
    if (excludeProductIds.length > 0) {
      options.exclude_product_ids = excludeProductIds;
      
    }
    
    if (includeCategoryIds.length > 0) {
      options.include_category_ids = includeCategoryIds;
      
    }
    
    if (excludeCategoryIds.length > 0) {
      options.exclude_category_ids = excludeCategoryIds;
      
    }
    
    // 카테고리 ID가 있으면 추가 (정수로 변환)
    if (categoryId) {
      const categoryIdInt = typeof categoryId === 'string' ? parseInt(categoryId, 10) : Number(categoryId);
      if (!isNaN(categoryIdInt)) {
        options.category_id = categoryIdInt;
        
      }
    }
    
    return options;
  };
  
  // 컴포넌트 언마운트 시 상품 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetProductsState());
    };
  }, [dispatch]);

  // 페이지네이션 데이터 업데이트
  useEffect(() => {
    // 상품당 최대 20개로 가정하고 총 페이지 수 계산
    if (products && products.length > 0) {
      // API에서 정확한 총 아이템 수를 반환하지 않는 경우 대비 로직
      // 서버에서 페이지당 아이템 수보다 적은 결과가 오면 마지막 페이지라고 가정
      if (products.length < itemsPerPage) {
        setTotalPages(currentPage);
        setTotalItems((currentPage - 1) * itemsPerPage + products.length);
      } else {
        // 정확한 총 아이템 수가 없으므로 최소 1페이지 더 있다고 가정
        setTotalPages(Math.max(totalPages, currentPage + 1));
        setTotalItems(Math.max(totalItems, currentPage * itemsPerPage));
      }
    } else if (products && products.length === 0 && currentPage > 1) {
      // 현재 페이지에 결과가 없고 현재 페이지가 1보다 큰 경우, 마지막 페이지를 벗어났다고 가정
      setTotalPages(currentPage - 1);
      setTotalItems((currentPage - 1) * itemsPerPage);
    }
  }, [products, currentPage, itemsPerPage]);

  // useEffect for filtering products
  useEffect(() => {
    try {
      
      // 상품 로드 중이면 API 호출 생략
      if (loading) {
        return;
      }
      
      // 필터 객체 생성
      const currentFilters = {
        categoryId: categoryId || "",
        include_product_ids: include_product_ids,
        exclude_product_ids: exclude_product_ids,
        include_category_ids: include_category_ids,
        exclude_category_ids: exclude_category_ids,
        page: currentPage,
        per_page: itemsPerPage
      };
      
      // 필터 문자열로 변환 (비교용)
      const currentFiltersStr = JSON.stringify(currentFilters);
      const prevFiltersStr = prevFiltersRef.current;
      
      // 초기 로드 완료 상태와 필터 비교
      const isFilterChanged = prevFiltersStr !== currentFiltersStr;
      
      
      
      // 이전 필터 저장
      prevFiltersRef.current = currentFiltersStr;
      
      // API 호출 여부 결정 - 핵심 부분을 수정
      // 1. 초기 로드가 아직 안됐거나
      // 2. 필터가 변경되었을 때만 API 호출 (products 의존성 제거)
      const needsApiCall = !initialLoadCompletedRef.current || isFilterChanged;
      
      
      if (needsApiCall) {
        
        // 현재 카테고리와 상품 ID에 따라 API 호출 옵션 설정
        const apiOptions = getFilterOptions();
        
        
        // 상품 로딩 액션 디스패치
        dispatch(fetchProducts(apiOptions));
        
        // 초기 로드 완료 표시
        initialLoadCompletedRef.current = true;
      } else {
        // API 호출 불필요 - 필터만 적용
        
        // 이곳에 필터링 로직 구현 (필요시)
      }
    } catch (error) {
      console.error("[오류] 필터링/API 호출 중 오류 발생:", error);
    }
  }, [
    categoryId,
    include_product_ids,
    exclude_product_ids,
    include_category_ids,
    exclude_category_ids,
    currentPage, // 페이지 번호가 변경되면 API 호출
    itemsPerPage, // 페이지당 아이템 수가 변경되면 API 호출
    loading,
    dispatch
    // products 의존성 제거 - 무한 루프 방지
  ]);

  // 컴포넌트가 렌더링될 때마다 카운터 증가
  useEffect(() => {
    setRenderCount(prev => prev + 1);
    
  }, []);

  // 윈도우 크기 변경 감지하여 PC에서는 항상 4개 상품 표시
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 767; 
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 991;
      
      if (!isMobile && !isTablet) {
        // PC 환경에서는 항상 4개로 강제 설정 (xlValue = 3)
        setXlValue(3);
      } else if (isTablet) {
        // 태블릿에서는 2개 (xlValue = 6)
        setXlValue(6);
      } else {
        // 모바일에서는 1개 (xlValue = 12)
        setXlValue(12);
      }
    };

    // 초기 실행
    handleResize();
    
    // 윈도우 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
    
    // 클린업 함수
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // itemsPerRow 값에 따라 xl 값 계산
  // 4개 -> xl=3, 3개 -> xl=4, 2개 -> xl=6, 6개 -> xl=2
  function getXlValue(items) {
    const itemCount = Number(items); // 문자열일 경우 숫자로 변환
    
    switch(itemCount) {
      case 2: return 6;
      case 3: return 4;
      case 6: return 2;
      default: return 3; // 기본값 4개 (xl=3)
    }
  }

  // itemsPerRow가 변경될 때마다 xlValue 업데이트
  useEffect(() => {
    // PC 환경에서는 항상 4개로 강제 설정
    const isMobile = window.innerWidth <= 767; 
    const isTablet = window.innerWidth > 767 && window.innerWidth <= 991;
    
    if (!isMobile && !isTablet) {
      // PC 환경에서는 항상 4개로 강제 설정 (xlValue = 3)
      setXlValue(3);
    } else {
      // 모바일 및 태블릿에서는 원래 로직 유지
      const newXlValue = getXlValue(itemsPerRow);
      setXlValue(newXlValue);
    }
  }, [itemsPerRow]);

  const toggleView = (isGrid) => {
    setIsGridView(isGrid);
  };

  // 한 줄에 표시할 항목 수에 따른 클래스 계산
  const getItemsPerRowClass = () => {
    return `items-per-row-${itemsPerRow}`;
  };
  
  // 페이지네이션 렌더링 함수
  const renderPagination = () => {
    // 표시할 페이지 버튼 수 계산
    const pageButtonsToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(pageButtonsToShow / 2));
    let endPage = Math.min(totalPages, startPage + pageButtonsToShow - 1);
    
    // 페이지 범위 조정
    if (endPage - startPage + 1 < pageButtonsToShow && totalPages > pageButtonsToShow) {
      startPage = Math.max(1, endPage - pageButtonsToShow + 1);
    }
    
    // 페이지 버튼 배열 생성
    const pageButtons = [];
    
    // 이전 페이지 버튼
    pageButtons.push(
      <li key="prev" className={currentPage === 1 ? "disabled" : ""}>
        <a href="#" onClick={(e) => {
          e.preventDefault();
          if (currentPage > 1) handlePageChange(currentPage - 1);
        }}>
          {t('이전')}
        </a>
      </li>
    );
    
    // 첫 페이지 버튼 (시작 페이지가 1이 아닌 경우)
    if (startPage > 1) {
      pageButtons.push(
        <li key="first">
          <a href="#" onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}>
            1
          </a>
        </li>
      );
      
      // 생략 표시 (시작 페이지가 2보다 크면)
      if (startPage > 2) {
        pageButtons.push(
          <li key="ellipsis1" className="disabled">
            <a href="#">...</a>
          </li>
        );
      }
    }
    
    // 페이지 번호 버튼
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li key={i} className={i === currentPage ? "active" : ""}>
          <a href="#" onClick={(e) => {
            e.preventDefault();
            handlePageChange(i);
          }}>
            {i}
          </a>
        </li>
      );
    }
    
    // 마지막 페이지 버튼 (끝 페이지가 totalPages보다 작은 경우)
    if (endPage < totalPages) {
      // 생략 표시 (끝 페이지가 totalPages-1보다 작으면)
      if (endPage < totalPages - 1) {
        pageButtons.push(
          <li key="ellipsis2" className="disabled">
            <a href="#">...</a>
          </li>
        );
      }
      
      pageButtons.push(
        <li key="last">
          <a href="#" onClick={(e) => {
            e.preventDefault();
            handlePageChange(totalPages);
          }}>
            {totalPages}
          </a>
        </li>
      );
    }
    
    // 다음 페이지 버튼
    pageButtons.push(
      <li key="next" className={currentPage === totalPages ? "disabled" : ""}>
        <a href="#" onClick={(e) => {
          e.preventDefault();
          if (currentPage < totalPages) handlePageChange(currentPage + 1);
        }}>
          {t('다음')}
        </a>
      </li>
    );
    
    return (
      <ul className="pagination" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {pageButtons}
      </ul>
    );
  };

  // 로딩 중 표시
  if (loading) {
    return (
      <div className="product-loading" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품 목록을 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 오류 발생 시 표시
  if (error && !products.length) {
    return (
      <div className="product-error" style={{padding: "20px", textAlign: "center", color: "red"}}>
        <p>{typeof error === 'object' ? error.message || JSON.stringify(error) : error}</p>
      </div>
    );
  }
  
  // 상품이 없는 경우
  if (!products.length) {
    return (
      <div className="no-products" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품이 없습니다.')}</p>
      </div>
    );
  }

  return (
    <section className="gi-shop">
      {/* 커스텀 CSS 추가 */}
      <style dangerouslySetInnerHTML={{ __html: customCSS }} />
      <div className="container">
        
        <Row className={`${className} ${getItemsPerRowClass()}`} style={{ margin:"0 auto" }}>
          <Col lg={lg} md={12} className={`margin-b-30 gi-shop-rightside ${order}`}>
            {/* Shop Top Start */}
            {/* <div className="gi-pro-list-top d-flex">
              <div className="col-md-6 gi-grid-list">
                <div className="gi-gl-btn">
                  <button
                    className={`grid-btn btn-grid-50 ${!isGridView ? "active" : ""}`}
                    onClick={() => toggleView(false)}
                  >
                    <i className="fas fa-th-large"></i>
                  </button>
                  <button
                    className={`grid-btn btn-list-50 ${isGridView ? "active" : ""}`}
                    onClick={() => toggleView(true)}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
              </div>
              <div className="col-md-6 gi-sort-select">
                <div className="gi-select-inner">
                  <select name="gi-select" id="gi-select" defaultValue="">
                    <option value="" disabled>Sort by</option>
                    <option value="1">Position</option>
                    <option value="2">Relevance</option>
                    <option value="3">Name, A to Z</option>
                    <option value="4">Name, Z to A</option>
                    <option value="5">Price, low to high</option>
                    <option value="6">Price, high to low</option>
                  </select>
                </div>
              </div>
            </div> */}
            {/* Shop Top End */}

            {/* Shop content Start */}
            <div className={`shop-pro-content ${isGridView ? "list-view-50" : ""}`}>
              <div className="shop-pro-inner">
                <Row style={{ margin:"0 auto" }}>
                  {products
                    .filter((_, index) => !excludeIndexes.includes(index.toString()) && !excludeIndexes.includes(index)) // 제외할 인덱스 필터링
                    .map((product, index) => (
                    <Col 
                      key={index}
                      xl={isGridView ? 12 : xlValue} // 상태로 관리되는 xlValue 사용
                      lg={isGridView ? 12 : 3} // PC에서 항상 4개 표시 (강제 설정)
                      md={isGridView ? 12 : 6} // 태블릿에서 2개 표시
                      sm={isGridView ? 12 : 6} // 작은 태블릿에서 2개 표시
                      xs={isGridView ? 12 : 12} // 모바일에서 1개 표시
                      className={`${isGridView ? "col-md-6 col-xs-6 col-sm-6 width-50" : ""} mb-6 gi-product-box pro-gl-content`}
                    >
                      <div className="gi-product-content">
                        <div className={`gi-product-inner ${isGridView ? "d-flex" : ""}`}>
                          <div className={`gi-pro-image-outer ${isGridView ? "flex-shrink-0" : ""}`} 
                            style={isGridView ? {width: '30%'} : productImageStyle.imgContainer}>
                            <div className={`gi-pro-image ${product.image ? "product-hover-area" : ""}`} 
                              style={isGridView ? 
                                {width: "100%", height: "300px", display: "flex", alignItems: "center", justifyContent: "center"} : 
                                productImageStyle.imgWrapper}>
                              <a 
                                href={`/products/${product.id}`} 
                                className="image" 
                                style={productImageStyle.imageLink}
                                onMouseEnter={product.image ? (e) => handleImageHover(e, true) : undefined}
                                onMouseLeave={product.image ? (e) => handleImageHover(e, false) : undefined}
                              >
                                <SafeImage
                                  src={product.image} 
                                  alt={product.title} 
                                  className="main-image"
                                  style={product.image ? productImageStyle.mainImage : {}}
                                  placeholderStyle={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0
                                  }}
                                />
                                {/* 호버 이미지는 imageTwo가 있을 때만 추가 */}
                                {product.image && product.imageTwo && (
                                  <SafeImage 
                                    src={product.imageTwo} 
                                    alt={product.title} 
                                    className="hover-image"
                                    style={productImageStyle.hoverImage}
                                  />
                                )}
                              </a>
                              {product.sale && (
                                <span className="flags">
                                  <span className="sale">{t(product.sale)}</span>
                                </span>
                              )}
                              
                              {/* 상품 액션 버튼 영역 추가 - 호버시 표시 */}
                              {product.image && (
                                <div className="product-action">
                                  <div className="pro-same-action pro-cart">
                                    <button
                                      title={t('장바구니에 추가')}
                                      onClick={(e) => {
                                        e.preventDefault(); // 이벤트 전파 방지
                                        e.stopPropagation(); // 상위 이벤트 방지
                                        handleAddToCart(product);
                                      }}
                                    >
                                      <i className="fas fa-shopping-cart"></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`gi-pro-content ${isGridView ? "flex-grow-1 ps-4" : ""}`}>
                            <h6 className="gi-pro-title">
                              <a href={`/products/${product.id}`}>{t(product.title)}</a>
                            </h6>
                            {/* <div className="gi-pro-rating">
                              {[...Array(5)].map((_, i) => (
                                <i 
                                  key={i} 
                                  className={`gicon ${i < product.rating ? "gi-star fill" : "gi-star-o"}`}
                                >
                                  {i < product.rating ? "★" : "☆"}
                                </i>
                              ))}
                            </div> */}
                            <div className="gi-pro-price">
                              {product.oldPrice > product.newPrice ? (
                                <DiscountRatePriceDisplay
                                  discountedPrice={product.newPrice}
                                  originalPrice={product.oldPrice}
                                  discountedPriceClassName="new-price"
                                  originalPriceClassName="old-price"
                                />
                              ) : (
                                <PriceDisplay price={product.newPrice} className="new-price" />
                              )}
                            </div>
                            {isGridView && (
                              <div className="gi-pro-desc">
                                <p>{product.description || t('상품 설명이 없습니다.')}</p>
                              </div>
                            )}
                            {/* 재고 상태 표시 */}
                            {showStock && (
                              <div className="gi-pro-stock">
                                <span className={`stock-status ${product.stockCount <= 10 && product.stockCount > 0 ? "low-stock" : ""}`}>
                                  {!product.isInStock
                                    ? <span style={{color: "red"}}>{t('품절')}</span> 
                                    : `${t('재고')}: ${product.stockCount}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
              {/* Pagination Start */}
              <div className="gi-pro-pagination">
                {totalPages > 1 && (
                  <>
                    <div className="pagination-info" style={{ textAlign: 'center', marginBottom: '10px' }}>
                      
                    </div>
                    {renderPagination()}
                  </>
                )}
              </div>
              {/* Pagination End */}
            </div>
            {/* Shop content End */}
          </Col>
          
        </Row>
      </div>
    </section>
  );
};

export default ProductListComponent;
